<template>
    <div class="p-grid">
        <div class="p-col-12 p-md-12">
            <div class="card p-fluid">

                <div class="flexed-container">
                    <h4 style="width: 200px">RFP Insights</h4>
                    <p>Please note that the collection of metrics for RFP Insights is enabled on all RFPs created on or after April 10th, 2025.  We are currently investigating adding Insights to RFPs created before that date.</p>
                </div>
            </div>

            <!-- Show tabs only for ArenaCX platform -->
            <TabView v-if="isArenaCxPlatform" v-model:activeIndex="activeTabIndex" @tab-change="onTabChange">
                <TabPanel header="ArenaCX">
                    <opportunities-content
                        :dates="dates"
                        :selectedTimeframe="selectedTimeframe"
                        :loading="loading"
                        :summaryData="summaryData"
                        :groupedRfps="groupedRfps"
                        :dataAccessCode="dataAccessCode"
                        @set-timeframe="setTimeframe"
                        @update-dates="updateDates"
                        @get-insights="getRfpListInsights"
                        @scroll-to-section="scrollToSection"
                        @navigate-to-rfp="navigateToRfpDetails"
                        @get-section-id="getSectionId"
                    />
                </TabPanel>
                <TabPanel v-if="false" header="White Label Partner">
                    <opportunities-content
                        :dates="datesCallCenter"
                        :selectedTimeframe="selectedTimeframeCallCenter"
                        :loading="loadingCallCenter"
                        :summaryData="summaryDataCallCenter"
                        :groupedRfps="groupedRfpsCallCenter"
                        :dataAccessCode="dataAccessCode"
                        :readOnly="true"
                        @set-timeframe="setTimeframeCallCenter"
                        @update-dates="updateDatesCallCenter"
                        @get-insights="getRfpListInsightsCallCenter"
                        @scroll-to-section="scrollToSection"
                        @navigate-to-rfp="navigateToRfpDetails"
                        @get-section-id="getSectionId"
                    />
                </TabPanel>
            </TabView>

            <!-- Default view for non-ArenaCX platform -->
            <opportunities-content
                v-else
                :dates="dates"
                :selectedTimeframe="selectedTimeframe"
                :loading="loading"
                :summaryData="summaryData"
                :groupedRfps="groupedRfps"
                :dataAccessCode="dataAccessCode"
                @set-timeframe="setTimeframe"
                @update-dates="updateDates"
                @get-insights="getRfpListInsights"
                @scroll-to-section="scrollToSection"
                @navigate-to-rfp="navigateToRfpDetails"
                @get-section-id="getSectionId"
            />
        </div>
    </div>
</template>

<script>
import { getRole, getMarketplaceUserAccountId } from '../service/common';
import RfpInsightsService from './../service/RfpInsightsService';
import { VENDOR_OPPORTUNITY_STATUS } from '../utilities/constants';
import OpportunitiesContent from './OpportunitiesContent.vue';

export default {
    components: {
        OpportunitiesContent
    },
    data() {
        return {
            products: null,
            dates: null,
            datesCallCenter: null,
            listPayload: null,
            showTelemetry: true,
            summaryData: [],
            summaryDataCallCenter: [],
            groupedRfps: {},
            groupedRfpsCallCenter: {},
            selectedTimeframe: 'current-quarter',
            selectedTimeframeCallCenter: 'current-quarter',
            loading: false,
            loadingCallCenter: false,
            userRole: null,
            adminOpportunityKey: null,
            partnerOpportunityKey: null,
            dataAccessCode: null,
            currentPartnerAccountId: null,
            activeTabIndex: 0,
            isArenaCxPlatform: false
        }
    },

    productService: null,
    rfpInsightsService: null,
    async created() {
        this.rfpInsightsService = new RfpInsightsService();
        this.userRole = getRole();
        this.currentPartnerAccountId = localStorage.getItem('accountId');
        this.isArenaCxPlatform = process.env.VUE_APP_PLATFORM_NAME === 'ArenaCX';
        console.log("Account ID of the Partner: ", this.currentPartnerAccountId);
        console.log("Is ArenaCX Platform: ", this.isArenaCxPlatform);

        this.adminOpportunityKey = process.env.VUE_APP_ADMIN_OPPORTUNITY_KEY;
        this.partnerOpportunityKey = process.env.VUE_APP_PARTNER_OPPORTUNITY_KEY;
        this.dataAccessCode = this.userRole === 'channel:business:admin' || this.userRole === 'acx:system:admin' ? this.adminOpportunityKey : this.partnerOpportunityKey;
        
        this.setInitialDateRange();
        if (this.isArenaCxPlatform) {
            this.setInitialDateRangeCallCenter();
        }
    },
    methods: {
        // A vendor can only see an RFP here once they've been invited (post-publish),
        // so "Rejected" (project_status_id 16) can never mean a pre-publish admin
        // rejection in this view - it always means the RFP was lost to another vendor.
        // Relabel to avoid vendors reading "Rejected" as their own proposal being rejected.
        getVendorFacingStatus(statusDisplayName) {
            return statusDisplayName === 'Rejected' ? 'Lost' : statusDisplayName;
        },

        getSectionId(status) {
            // Convert status to lowercase and replace spaces and slashes with hyphens
            // First replace multiple spaces with a single space, then convert to lowercase and replace spaces with hyphens
            const id = status.toLowerCase().trim().replace(/\s+/g, '-');
            console.log('Generated ID for status:', status, 'is:', id);
            return id;
        },

        scrollToSection(sectionId) {
            console.log('Scrolling to section:', sectionId);
            const element = document.getElementById(sectionId);
            console.log('Found element:', element);
            if (element) {
                const offset = 80; // Add 100px margin top
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - offset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        },

        // Navigate to RFP details page
        navigateToRfpDetails(id, encId) {
            console.log('Navigating with ID:', id, 'and encId:', encId);
            
            // If encId is null, use old flow (external RFPPerfect URL)
            if (!encId) {
                console.log('Legacy RFP detected - redirecting to historical details page');
                const baseUrl = window.location.origin;
                const vendorId = encodeURIComponent(this.currentPartnerAccountId);
                window.open(`${baseUrl}/#/partner/legacy-rfp/${id}?vendorId=${vendorId}`, '_blank');
            } else {
                // If encId is not null, use new flow (Partner Opportunity page)
                console.log('Using new flow - navigating to Partner Opportunity page');
                const baseUrl = window.location.origin;
                window.open(`${baseUrl}/#/partner-opportunity?id=${encId}`, '_blank');
            }
        },

        // Set initial date range
        setInitialDateRange() {
            const today = new Date();
            const currentQuarter = Math.floor(today.getMonth() / 3);
            const firstDayOfCurrentQuarter = new Date(today.getFullYear(), currentQuarter * 3, 1);
            const lastDayOfCurrentQuarter = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);

            this.dates = [firstDayOfCurrentQuarter, lastDayOfCurrentQuarter];
            this.getRfpListInsights(); // Call the API with initial date range
        },

        setInitialDateRangeCallCenter() {
            const today = new Date();
            const currentQuarter = Math.floor(today.getMonth() / 3);
            const firstDayOfCurrentQuarter = new Date(today.getFullYear(), currentQuarter * 3, 1);
            const lastDayOfCurrentQuarter = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);

            this.datesCallCenter = [firstDayOfCurrentQuarter, lastDayOfCurrentQuarter];
        },

        updateDates(newDates) {
            this.dates = newDates;
        },

        updateDatesCallCenter(newDates) {
            this.datesCallCenter = newDates;
        },

        setTimeframe(timeframe) {
            this.selectedTimeframe = timeframe;
            const today = new Date();
            let start = new Date();
            let end = new Date();

            switch (timeframe) {
                case 'prev-week':
                    start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
                    end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
                    break;
                case 'prev-month':
                    start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    end = new Date(today.getFullYear(), today.getMonth(), 0);
                    break;
                case 'prev-quarter': {
                    const quarter = Math.floor(today.getMonth() / 3);
                    start = new Date(today.getFullYear(), (quarter - 1) * 3, 1);
                    end = new Date(today.getFullYear(), quarter * 3, 0);
                    break;
                }
                case 'current-week': {
                    const day = today.getDay();
                    start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - day);
                    end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + (6 - day));
                    break;
                }
                case 'current-month':
                    start = new Date(today.getFullYear(), today.getMonth(), 1);
                    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                    break;
                case 'current-quarter': {
                    const currentQuarter = Math.floor(today.getMonth() / 3);
                    start = new Date(today.getFullYear(), currentQuarter * 3, 1);
                    end = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);
                    break;
                }
            }

            this.dates = [start, end];
            this.getRfpListInsights();
        },

        setTimeframeCallCenter(timeframe) {
            this.selectedTimeframeCallCenter = timeframe;
            const today = new Date();
            let start = new Date();
            let end = new Date();

            switch (timeframe) {
                case 'prev-week':
                    start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
                    end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
                    break;
                case 'prev-month':
                    start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    end = new Date(today.getFullYear(), today.getMonth(), 0);
                    break;
                case 'prev-quarter': {
                    const quarter = Math.floor(today.getMonth() / 3);
                    start = new Date(today.getFullYear(), (quarter - 1) * 3, 1);
                    end = new Date(today.getFullYear(), quarter * 3, 0);
                    break;
                }
                case 'current-week': {
                    const day = today.getDay();
                    start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - day);
                    end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + (6 - day));
                    break;
                }
                case 'current-month':
                    start = new Date(today.getFullYear(), today.getMonth(), 1);
                    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                    break;
                case 'current-quarter': {
                    const currentQuarter = Math.floor(today.getMonth() / 3);
                    start = new Date(today.getFullYear(), currentQuarter * 3, 1);
                    end = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);
                    break;
                }
            }

            this.datesCallCenter = [start, end];
            this.getRfpListInsightsCallCenter();
        },

        onTabChange(event) {
            // When switching to White Label Partner tab, load data if not already loaded
            if (event.index === 1 && !this.summaryDataCallCenter.length && !this.loadingCallCenter) {
                this.getRfpListInsightsCallCenter();
            }
        },

        // Get RFP list insights for ArenaCX
        async getRfpListInsights() {
            try {
                this.loading = true;
                this.listPayload = {
                    startDate: new Date(this.dates[0].getTime() - this.dates[0].getTimezoneOffset() * 60000).toISOString().split('T')[0] + 'T00:00:00.000Z',
                    endDate: new Date(this.dates[1].getTime() - this.dates[1].getTimezoneOffset() * 60000).toISOString().split('T')[0] + 'T23:59:59.999Z',
                    partnerId: localStorage.getItem("accountId"),
                }
                await this.processRfpListInsights(this.listPayload, false);
            } finally {
                this.loading = false;
            }
        },

        // Get RFP list insights for White Label Partner
        async getRfpListInsightsCallCenter() {
            try {
                this.loadingCallCenter = true;
                this.listPayload = {
                    startDate: new Date(this.datesCallCenter[0].getTime() - this.datesCallCenter[0].getTimezoneOffset() * 60000).toISOString().split('T')[0] + 'T00:00:00.000Z',
                    endDate: new Date(this.datesCallCenter[1].getTime() - this.datesCallCenter[1].getTimezoneOffset() * 60000).toISOString().split('T')[0] + 'T23:59:59.999Z',
                    partnerId: localStorage.getItem("accountId"),
                    subnetworkKey: process.env.VUE_APP_WHITE_LABEL_PARTNER_SUBNETWORK_KEY
                }
                await this.processRfpListInsights(this.listPayload, true);
            } finally {
                this.loadingCallCenter = false;
            }
        },

        // Process RFP list insights data
        async processRfpListInsights(listPayload, isCallCenter) {
            const res = await this.rfpInsightsService.getRfpListInsights(listPayload);

            // Process data for summary table
            const statusCounts = {
                rfpActionRequired: 0,
                invitesReceived: 0,
                rfpsDeclined: 0,
                rfpsNotResponded: 0,
                rfpsParticipated: 0,
                rfpsNotAwarded: 0,
                rfpsAwarded: 0,
                rfpsSigned: 0
            };

            // Initialize grouped RFPs
            const groupedRfps = {
                'RFP Action Required': [],
                'Invites Received': [],
                'RFPs Declined': [],
                'RFPs Not Responded To': [],
                'RFPs Participated In': [],
                'RFPs Not Awarded': [],
                'RFPs Awarded': [],
                'RFPs Signed': []
            };

            const partnerId = localStorage.getItem("accountId");

            // Set total invites received
            statusCounts.invitesReceived = res.data.length;
            groupedRfps['Invites Received'] = res.data.map(item => ({
                id: item.id,
                name: item.name,
                status: this.getVendorFacingStatus(item.status_display_name),
                startDate: item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A',
                endDate: item.end_date ? new Date(item.end_date).toLocaleDateString() : 'N/A',
                encId: item.rfp_encid
            }));

            // Process each RFP for other categories
            res.data.forEach(item => {
                const processedItem = {
                    id: item.id,
                    name: item.name,
                    status: this.getVendorFacingStatus(item.status_display_name),
                    startDate: item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A',
                    endDate: item.end_date ? new Date(item.end_date).toLocaleDateString() : 'N/A',
                    encId: item.rfp_encid
                };

                // RFP Action Required - check for specific status display names and vendor opportunity status
                const currentUserAccountId = localStorage.getItem("accountId");
                const isRfpActionRequired = (
                    // Case 1: Receive Vendor Responses with specific vendor statuses
                    (item.status_display_name === 'Receive Vendor Responses' && 
                     [VENDOR_OPPORTUNITY_STATUS["INVITATION MAIL SENT"], 
                      VENDOR_OPPORTUNITY_STATUS["ACCEPTED TERMS & CONDITIONS"], 
                      VENDOR_OPPORTUNITY_STATUS["ACCEPTED TO PARTICIPATE IN EVENT"],
                      VENDOR_OPPORTUNITY_STATUS["RESPONSE RETRACTED"],
                      VENDOR_OPPORTUNITY_STATUS["RETRACTED ON BEHALF OF"],
                      VENDOR_OPPORTUNITY_STATUS["RESPONSE DRAFT"]].includes(item.vendor_status) && new Date(item.end_date) > new Date()) ||
                    // Case 2: Execute Your Contract and current user is the selected vendor
                    (item.status_display_name === 'Execute Your Contract' && 
                     item.selected_vendor === currentUserAccountId)
                );
                
                if (isRfpActionRequired) {
                    statusCounts.rfpActionRequired++;
                    groupedRfps['RFP Action Required'].push(processedItem);
                }

                // RFPs Participated In
                if ([9, 12, 13, 14, 10].includes(item.vendor_status) &&
                    [2, 5, 6, 3, 16].includes(item.status)) {
                    statusCounts.rfpsParticipated++;
                    groupedRfps['RFPs Participated In'].push(processedItem);
                }
                // RFPs Declined
                if (item.vendor_status === 17) {
                    statusCounts.rfpsDeclined++;
                    groupedRfps['RFPs Declined'].push(processedItem);
                }

                // RFPs Not Awarded
                if (item.selected_vendor &&
                    item.selected_vendor !== partnerId &&
                    [6, 3].includes(item.status) &&
                    [10, 12].includes(item.vendor_status)) {
                    statusCounts.rfpsNotAwarded++;
                    groupedRfps['RFPs Not Awarded'].push(processedItem);
                }

                // RFPs Not Responded To
                if ([5, 6, 3].includes(item.status) &&
                    [7, 8, 9, 13, 14, 11].includes(item.vendor_status)) {
                    statusCounts.rfpsNotResponded++;
                    groupedRfps['RFPs Not Responded To'].push(processedItem);
                }

                // RFPs In Contracting
                if ((item.status === 6 || item.status === 3) && item.selected_vendor === partnerId) {
                    statusCounts.rfpsAwarded++;
                    groupedRfps['RFPs Awarded'].push(processedItem);
                }

                // RFPs Signed
                if (item.status === 3 && item.selected_vendor === partnerId) {
                    statusCounts.rfpsSigned++;
                    groupedRfps['RFPs Signed'].push(processedItem);
                }
            });

            if (isCallCenter) {
                this.summaryDataCallCenter = [statusCounts];
                this.groupedRfpsCallCenter = groupedRfps;
            } else {
                this.summaryData = [statusCounts];
                this.groupedRfps = groupedRfps;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.opportunityLink {
    color: darkgreen;
    text-decoration: underline;
    cursor: pointer;
}

.calender-wrapper {
    display: flex;
    margin-top: 10px;
    margin-bottom: 16px;
    gap: 10px;
}

.calendar-container {
    width: 200px;
}

.empty-state {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;
}

.timeframe-selector {
    .timeframe-buttons {
        display: flex;
        gap: 2rem;

        .button-group {
            display: flex;
            align-items: center;
            gap: 0.5rem;

            h6 {
                margin: 0;
                min-width: 70px;
            }

            button {
                min-width: 100px;
            }
        }
    }
}

.status-section {
    margin-top: 2rem;

    h5 {
        margin-bottom: 1rem;
    }
}

.wrap-column {
    max-width: 300px;
}

.wrap-text {
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
}

.p-button:enabled:focus {
    background: #00796b !important;
}

.loader {
    display: flex;
    justify-content: center;
    margin: 2rem auto;
}

.flexed-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
}


// Add these new styles
:deep(.p-datatable) {
    .p-datatable-header {
        background-color: #e4edd9 !important;
        font-size: 1.1rem;
        font-weight: bold;
    }

    .p-datatable-thead > tr > th {
        background-color: #e4edd9 !important;
        color: #333;
        font-size: 1.1rem;
        font-weight: bold;
        padding: 1rem;
        border: 1px solid #ddd;
    }

    .p-datatable-tbody > tr > td {
        padding: 0.75rem 1rem;
        border: 1px solid #ddd;
    }

    // Add subtle hover effect on rows
    .p-datatable-tbody > tr:hover {
        background-color: #f8f9fa;
    }

    // Add subtle shadow to the table
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.summary-link {
    color: #00796b;
    text-decoration: underline;
    font-weight: bold;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
        color: #004d40;
        text-decoration: underline;
    }
}

</style>
